import React, { useState } from "react";
import { Button, Card, Table, Modal, Space, Tooltip, Empty, message } from "antd";
import moment from "moment";
import { dateFormat, modal } from "../../Tasks/configs/designTokens";
import MediaFetcher from "../library/MediaFetcher";
import { useRemoveAdUnitsFromGroup } from "../../Creatives/hooks/useRemoveAdUnitsFromGroup";
import DownloadAdUnitButton from "./DownloadAdUnitButton";
import EditDisplayAdUnitsModal from "./EditDisplayAdUnitsModal";
import { useMediaQuery } from "../hooks/useMediaQuery";

const AdGroupMediaCard = ({
  mediaType = "DISPLAY", // "DISPLAY" or "VIDEO"
  orgMedias,
  adgroupinfo,
  refetchAdGroupInfo,
  adGroupId,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { removeAdUnits } = useRemoveAdUnitsFromGroup();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [removeConfirmVisible, setRemoveConfirmVisible] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Dynamic labels based on media type
  const isVideo = mediaType === "VIDEO";
  const unitLabel = isVideo ? "VIDEO AD UNITS IN GROUP" : "DISPLAY AD UNITS IN GROUP";
  const buttonLabel = isVideo ? "Add Video Ad Units" : "Add Display Ad Units";
  const modalTitle = isVideo ? "Edit Video Ad Units" : "Edit Display Ad Units";

  // Row selection for delete adunit
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
    columnWidth: 32,
    getCheckboxProps: record => ({
      disabled: false,
      name: record.name,
    }),
  };

  // Row selection for download adunit
  const selectedKeys =
    adgroupinfo &&
    adgroupinfo.adGroupCreatives &&
    adgroupinfo.adGroupCreatives.medias
      ? adgroupinfo.adGroupCreatives.medias
          .filter(media => selectedRowKeys.includes(media.id))
          .map(media => media.key)
      : [];

  return (
    <Card
      title={
        <Space direction="vertical" className="creative-detail-card__full-width">
          <span>
            {adgroupinfo &&
              adgroupinfo.adGroupCreatives &&
              adgroupinfo.adGroupCreatives.medias &&
              adgroupinfo.adGroupCreatives.medias.length}{" "}
            {unitLabel}
          </span>
          <Space className="creative-detail-card__toolbar-row">

            <Space>
              <Button
                type="default"
                danger
                disabled={selectedRowKeys.length === 0 || (
                  adgroupinfo &&
                  adgroupinfo.adGroupCreatives &&
                  adgroupinfo.adGroupCreatives.medias &&
                  selectedRowKeys.length === adgroupinfo.adGroupCreatives.medias.length
                )}
                onClick={() => {
                  // Check if removing selected units would delete all units
                  if (
                    adgroupinfo &&
                    adgroupinfo.adGroupCreatives &&
                    adgroupinfo.adGroupCreatives.medias &&
                    selectedRowKeys.length === adgroupinfo.adGroupCreatives.medias.length
                  ) {
                    message.error('Cannot remove all ad units from the group. An ad group must contain at least one unit.');
                    return;
                  }

                  setRemoveConfirmVisible(true);
                }}
              >
                Remove
              </Button>
              <DownloadAdUnitButton
                creativeKeys={selectedKeys}
                disabled={selectedRowKeys.length === 0}
              />
            </Space>
            <Space>
              <Button
                type="default"
                onClick={() => setEditModalVisible(true)}
              >
                {buttonLabel}
              </Button>
            </Space>
          </Space>
        </Space>
      }
      size="default"
      className="creative-detail-card"
    >
      {isMobile ? (
        (() => {
          const medias = adgroupinfo?.adGroupCreatives?.medias || [];
          if (medias.length === 0) {
            return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No ad units in this group" />;
          }
          return (
            <div className="creative-media-cards">
              {medias.map((media) => (
                <div
                  key={media.id}
                  className={`creative-media-card${selectedRowKeys.includes(media.id) ? ' creative-media-card--selected' : ''}`}
                  onClick={() => {
                    setSelectedRowKeys((prev) =>
                      prev.includes(media.id)
                        ? prev.filter((k) => k !== media.id)
                        : [...prev, media.id]
                    );
                  }}
                >
                  <div
                    className="creative-media-card__thumb"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewKey(media.key);
                    }}
                  >
                    {media.key && (
                      <MediaFetcher
                        mediaKey={media.key}
                        width="100%"
                        height="100%"
                        objectFit="cover"
                      />
                    )}
                  </div>
                  <div className="creative-media-card__info">
                    <div className="creative-media-card__name">{media.name}</div>
                    <div className="creative-media-card__meta">
                      {media.width && media.height ? `${media.width}x${media.height}` : ""}
                      {media.fileType ? ` \u00B7 ${media.fileType}` : ""}
                    </div>
                    <div className="creative-media-card__date">
                      {media.createdAt ? moment(media.createdAt).format(dateFormat.short) : "\u2014"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      ) : (
        <div className="creative-table">
          <Table
            size="middle"
            rowSelection={rowSelection}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 832 }}
            rowKey="id"
            columns={[
              {
                title: "Preview",
                dataIndex: "preview",
                key: "preview",
                width: 100,
                align: "left",
                render: (_, record) => (
                  <div
                    className="creative-detail-card__thumbnail creative-detail-card__thumbnail--clickable"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewKey(record.key);
                    }}
                  >
                    <MediaFetcher
                      mediaKey={record.key}
                      className="creative-detail-card__media-thumb"
                    />
                  </div>
                ),
              },
              {
                title: "Name",
                dataIndex: "name",
                key: "name",
                width: 250,
                align: "left",
                ellipsis: true,
                sorter: (a, b) => a.name.localeCompare(b.name),
                render: text => (
                  text && text.length > 30 ? (
                    <Tooltip title={text}>
                      <span>{text.slice(0, 30).concat("...")}</span>
                    </Tooltip>
                  ) : (
                    <span>{text}</span>
                  )
                ),
              },
              {
                title: "Aspect",
                dataIndex: "aspect",
                key: "aspect",
                width: 100,
                align: "left",
                sorter: (a, b) => a.width - b.width || a.height - b.height,
                render: (_, record) =>
                  record.width && record.height
                    ? `${record.width}x${record.height}`
                    : "\u2014",
              },
              {
                title: "File Type",
                dataIndex: "fileType",
                key: "fileType",
                width: 100,
                align: "left",
                sorter: (a, b) => a.fileType.localeCompare(b.fileType),
              },
              {
                title: "Date Uploaded",
                dataIndex: "createdAt",
                key: "createdAt",
                width: 180,
                align: "left",
                sorter: (a, b) =>
                  moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf(),
                render: d =>
                  d ? moment(d).format(dateFormat.full) : "\u2014",
              },
            ]}
            bordered={false}
            dataSource={
              adgroupinfo &&
              adgroupinfo.adGroupCreatives &&
              adgroupinfo.adGroupCreatives.medias
                ? adgroupinfo.adGroupCreatives.medias
                : []
            }
          />
        </div>
      )}
      <Modal
        open={!!previewKey}
        footer={null}
        closable={false}
        centered
        destroyOnClose
        onCancel={() => setPreviewKey(null)}
        className="creative-video-preview-modal creative-preview-modal--responsive"
        maskClosable
      >
        {previewKey && (
          <MediaFetcher
            mediaKey={previewKey}
            className="creative-detail-card__media-preview"
          />
        )}
      </Modal>
      <Modal
        open={removeConfirmVisible}
        title="Remove Ad Units"
        width={modal.sm}
        centered
        wrapClassName="tags-modal"
        onCancel={() => setRemoveConfirmVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setRemoveConfirmVisible(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={async () => {
                try {
                  await removeAdUnits(
                    adgroupinfo.adGroupCreatives,
                    selectedRowKeys
                  );
                  setSelectedRowKeys([]);
                  setRemoveConfirmVisible(false);
                  await refetchAdGroupInfo();
                  message.success(`Successfully removed ${selectedRowKeys.length} ad unit(s) from the group`);
                } catch (error) {
                  message.error('Failed to remove ad units. Please try again.');
                }
              }}
            >
              Remove
            </Button>
          </div>
        }
      >
        <p>
          Are you sure you want to remove {selectedRowKeys.length} ad unit(s) from this group?
          This action cannot be undone.
        </p>
      </Modal>
      <EditDisplayAdUnitsModal
        open={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        adGroupId={adGroupId}
        orgMedias={orgMedias || []}
        title={modalTitle}
        initialSelected={
          adgroupinfo &&
          adgroupinfo.adGroupCreatives &&
          adgroupinfo.adGroupCreatives.medias
            ? adgroupinfo.adGroupCreatives.medias.map(m => m.id)
            : []
        }
        onSubmit={refetchAdGroupInfo}
      />
    </Card>
  );
};

export default AdGroupMediaCard;